/**
 * Client-side hook for real-time job notifications
 * 
 * Vercel-compatible approach:
 * 1. Short polling against Red Redis-backed endpoint (5s interval)
 * 2. Web Push Notifications for background updates (PWA)
 * 3. Falls back to standard polling if needed
 * 
 * Usage:
 * ```tsx
 * function MyComponent() {
 *   const queryClient = useQueryClient();
 *   
 *   useJobNotifications({
 *     onJobComplete: (event) => {
 *       queryClient.invalidateQueries({ queryKey: ['recentDrawings'] });
 *       toast.success('Analysis complete!');
 *     }
 *   });
 *   
 *   return <div>...</div>;
 * }
 * ```
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '@/src/components/AuthContextProvider';

export interface JobCompleteEvent {
  type: 'job:complete';
  jobId: string;
  drawingId?: string;
  userId?: string;
  status: 'DONE' | 'FAILED';
  timestamp: string;
}

export interface UseJobNotificationsOptions {
  /**
   * Called when a job completes (success or failure)
   */
  onJobComplete?: (event: JobCompleteEvent) => void;
  
  /**
   * Enable/disable notifications (default: true)
   */
  enabled?: boolean;
  
  /**
   * Polling interval in milliseconds (default: 5000)
   */
  pollInterval?: number;
}

/**
 * Hook to listen for real-time job notifications via Redis polling + Web Push
 */
export function useJobNotifications(options: UseJobNotificationsOptions = {}) {
  const { enabled = true, onJobComplete, pollInterval = 5000 } = options;
  const { user } = useAuth();
  const lastEventIdRef = useRef<string>('0');
  const pollingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isPollingRef = useRef(false);

  useEffect(() => {
    if (!enabled || !user?.id) {
      return;
    }

    const poll = async () => {
      if (isPollingRef.current) return; // Prevent concurrent polls
      isPollingRef.current = true;

      try {
        const userId = user.id;
        const response = await fetch(
          `/api/notifications/poll?userId=${userId}&lastEventId=${lastEventIdRef.current}`
        );

        if (!response.ok) {
          console.error('[Notifications] Poll failed:', response.status);
          return;
        }

        const data = await response.json();
        
        if (data.events && data.events.length > 0) {
          // Process new events
          for (const event of data.events) {
            if (event.type === 'job:complete' && onJobComplete) {
              onJobComplete(event as JobCompleteEvent);
            }
          }
          
          // Update last event ID
          lastEventIdRef.current = data.lastEventId;
        }
      } catch (error) {
        console.error('[Notifications] Poll error:', error);
      } finally {
        isPollingRef.current = false;
        
        // Schedule next poll
        if (enabled && user?.id) {
          pollingTimeoutRef.current = setTimeout(poll, pollInterval);
        }
      }
    };

    // Start polling
    poll();

    // Cleanup on unmount or when enabled changes
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [enabled, user?.id, onJobComplete, pollInterval]);

  return {
    connected: enabled && !!user?.id,
  };
}
